import { useEffect, useState } from "react";
import { CloudLightning, CloudRain, CloudSnow, Sun } from "lucide-react";
import { useVillageStore } from "./store";
import { nextChange, weatherAt, weekAhead, type DayPattern } from "./world/weather";
import type { WeatherType } from "./world/voxelTypes";

/**
 * The week ahead over the village.
 *
 * Nothing here is fetched. The weather is a function of the clock and the guild's world seed, so
 * the forecast is computed on the spot and every member of a guild is reading the same one — the
 * point of showing it at all is that "come over on Thursday, it's going to snow" is a thing
 * somebody can now say and be right about.
 *
 * Days are the village's days, which run on UTC like the day cycle. Times are the member's own,
 * because "when will it rain" is a question about their afternoon.
 */

const PATTERN_ICON: Record<DayPattern, typeof Sun> = {
  clear: Sun,
  "rain-arriving": CloudRain,
  "rain-clearing": CloudRain,
  storm: CloudLightning,
  snow: CloudSnow
};

const PATTERN_LABEL: Record<DayPattern, string> = {
  clear: "Clear",
  "rain-arriving": "Rain later",
  "rain-clearing": "Clearing",
  storm: "Storms",
  snow: "Snow"
};

const WEATHER_NOW: Record<WeatherType, string> = {
  clear: "Clear over the village",
  rain: "Raining",
  snow: "Snowing",
  storm: "Thunderstorm overhead"
};

function atHour(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/**
 * "Today", then the village's own weekday.
 *
 * Deliberately not "Tomorrow": the strip is seven equal columns and that word is twice the width
 * of the rest, which drags the whole grid out of alignment for no information anyone lacks.
 */
function dayLabel(startsAt: number, offset: number): string {
  if (offset === 0) return "Today";
  return new Date(startsAt).toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });
}

/** What is happening now, and what happens next if the day has a turn left in it. */
function todayLine(now: number, seed: string): string {
  const nowWeather = weatherAt(now, seed);
  const change = nextChange(now, seed);
  const opening = WEATHER_NOW[nowWeather];
  if (!change) return `${opening}, and settled for the day`;
  if (change.weather === "clear") return `${opening}, clearing from ${atHour(change.at)}`;
  if (change.weather === "storm") return `${opening}, storm breaking around ${atHour(change.at)}`;
  if (nowWeather === "storm") return `${opening}, easing to rain around ${atHour(change.at)}`;
  return `${opening}, rain from ${atHour(change.at)}`;
}

export function Forecast() {
  const seed = useVillageStore((state) => state.data.world.seed);
  const [now, setNow] = useState(() => Date.now());

  // The panel can sit open across a turn in the weather, and a forecast that says it is raining
  // while the window behind it clears would be worse than not showing one.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const week = weekAhead(now, seed);

  return (
    <section className="panel-section">
      <div className="section-heading">
        <h3>Forecast</h3>
        <span>The same sky for everyone</span>
      </div>
      <p className="forecast-now">{todayLine(now, seed)}</p>
      <div className="forecast-week">
        {week.map((day, offset) => {
          const Icon = PATTERN_ICON[day.pattern];
          return (
            <div
              key={day.dayIndex}
              className={offset === 0 ? "forecast-day is-today" : "forecast-day"}
              title={`${dayLabel(day.startsAt, offset)}: ${PATTERN_LABEL[day.pattern]}`}
            >
              <span>{dayLabel(day.startsAt, offset)}</span>
              <Icon size={18} />
              <small>{PATTERN_LABEL[day.pattern]}</small>
            </div>
          );
        })}
      </div>
    </section>
  );
}
